import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";

// Tour produit guidé. Lit la config des steps depuis le Ruby helper
// (sérialisée dans data-platform-tour-steps-value) et orchestre :
//   - une welcome modal (centrée, non ancrée à un élément)
//   - une séquence de popovers ancrées à des éléments DOM (Popper.js)
//   - une spotlight (overlay sombre + trou autour de la cible)
//   - une final modal
//
// Persistance : POST /platform_tour/advance à chaque step, /skip pour skip
// définitif, /complete à la fin.
export default class extends Controller {
  static targets = [
    "welcomeModal", "finalModal",
    "popover", "popoverTitle", "popoverBody", "popoverIcon",
    "prevBtn", "nextBtn", "stepCounter",
    "spotlight",
  ];

  static values = {
    steps:        Array,
    currentStep:  { type: Number, default: 0 },
    advanceUrl:   String,
    skipUrl:      String,
    completeUrl:  String,
    autostart:    { type: Boolean, default: false },
  };

  connect() {
    this._popper = null;
    this._onResize = this._handleResize.bind(this);
    this._onKey = this._handleKey.bind(this);
    window.addEventListener("resize", this._onResize, { passive: true });
    window.addEventListener("scroll", this._onResize, { passive: true });
    document.addEventListener("keydown", this._onKey);

    if (this.autostartValue) {
      // Léger délai pour laisser la page se stabiliser (images, layout shift).
      setTimeout(() => this._render(), 200);
    }
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("scroll", this._onResize);
    document.removeEventListener("keydown", this._onKey);
    this._popper?.destroy();
    document.body.classList.remove("pt-no-scroll");
  }

  // ── Public actions ──────────────────────────────────────────────────

  start(event) {
    event?.preventDefault();
    // Welcome est l'index 0 ; on saute à 1 (premier step ancré).
    this.currentStepValue = 1;
    this._render();
    this._persistAdvance();
  }

  next(event) {
    event?.preventDefault();
    if (this.currentStepValue >= this.stepsValue.length - 1) {
      this.complete();
      return;
    }
    this.currentStepValue += 1;
    this._render();
    this._persistAdvance();
  }

  prev(event) {
    event?.preventDefault();
    if (this.currentStepValue <= 1) return; // pas de retour vers welcome
    this.currentStepValue -= 1;
    this._render();
    this._persistAdvance();
  }

  skip(event) {
    event?.preventDefault();
    this._teardown();
    fetch(this.skipUrlValue, { method: "POST", headers: this._headers() }).catch(() => {});
  }

  complete(event) {
    event?.preventDefault();
    this._teardown();
    fetch(this.completeUrlValue, { method: "POST", headers: this._headers() }).catch(() => {});
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _render() {
    const step = this.stepsValue[this.currentStepValue];
    if (!step) return this._teardown();

    if (step.variant === "modal") {
      this._showModal(step);
      return;
    }
    this._showPopover(step);
  }

  _showModal(step) {
    this._hidePopover();
    this._hideSpotlight();
    const target = step.kind === "welcome"
                   ? (this.hasWelcomeModalTarget ? this.welcomeModalTarget : null)
                   : (this.hasFinalModalTarget   ? this.finalModalTarget   : null);
    if (!target) return;
    target.hidden = false;
    requestAnimationFrame(() => target.classList.add("pt-modal--visible"));
    document.body.classList.add("pt-no-scroll");
  }

  _showPopover(step) {
    if (this.hasWelcomeModalTarget) {
      this.welcomeModalTarget.classList.remove("pt-modal--visible");
      this.welcomeModalTarget.hidden = true;
    }
    if (this.hasFinalModalTarget) {
      this.finalModalTarget.classList.remove("pt-modal--visible");
      this.finalModalTarget.hidden = true;
    }

    const targetEl = step.selector ? document.querySelector(step.selector) : null;
    if (!targetEl) {
      this._skipMissingStep();
      return;
    }

    targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
    document.body.classList.add("pt-no-scroll");

    setTimeout(() => {
      this._positionSpotlight(targetEl);
      this._fillPopover(step);
      this._positionPopover(targetEl, step.placement || "auto");
    }, 220);
  }

  _fillPopover(step) {
    if (this.hasPopoverTitleTarget) this.popoverTitleTarget.textContent = step.title || "";
    if (this.hasPopoverBodyTarget)  this.popoverBodyTarget.textContent  = step.body  || "";
    if (this.hasPopoverIconTarget) {
      this.popoverIconTarget.className = `fa-solid ${step.icon || "fa-circle-info"}`;
    }
    if (this.hasStepCounterTarget) {
      const total = this.stepsValue.filter(s => s.variant !== "modal").length;
      const upto  = this.stepsValue.slice(0, this.currentStepValue + 1).filter(s => s.variant !== "modal").length;
      this.stepCounterTarget.textContent = `${upto} / ${total}`;
    }
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentStepValue <= 1;
    }
    if (this.hasNextBtnTarget) {
      const isLastBeforeFinal = this.currentStepValue >= this.stepsValue.length - 2;
      const label = this.nextBtnTarget.querySelector("[data-label]");
      if (label) label.textContent = isLastBeforeFinal ? "Terminer" : "Suivant";
    }
  }

  _positionSpotlight(targetEl) {
    if (!this.hasSpotlightTarget) return;
    const rect = targetEl.getBoundingClientRect();
    const padding = 8;
    const top    = Math.max(0, rect.top    - padding);
    const left   = Math.max(0, rect.left   - padding);
    const width  = rect.width  + padding * 2;
    const height = rect.height + padding * 2;

    Object.assign(this.spotlightTarget.style, {
      display: "block",
      top:    `${top}px`,
      left:   `${left}px`,
      width:  `${width}px`,
      height: `${height}px`,
    });
  }

  _hideSpotlight() {
    if (this.hasSpotlightTarget) this.spotlightTarget.style.display = "none";
  }

  _positionPopover(targetEl, placement) {
    if (!this.hasPopoverTarget) return;
    this.popoverTarget.hidden = false;
    requestAnimationFrame(() => this.popoverTarget.classList.add("pt-popover--visible"));

    if (this._popper) {
      this._popper.destroy();
      this._popper = null;
    }
    this._popper = createPopper(targetEl, this.popoverTarget, {
      placement: placement === "auto" ? "bottom" : placement,
      modifiers: [
        { name: "offset", options: { offset: [0, 16] } },
        { name: "preventOverflow", options: { padding: 16 } },
        { name: "flip", options: { fallbackPlacements: ["top", "bottom", "right", "left"] } },
      ],
    });
  }

  _hidePopover() {
    if (this._popper) { this._popper.destroy(); this._popper = null; }
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.remove("pt-popover--visible");
      this.popoverTarget.hidden = true;
    }
  }

  _skipMissingStep() {
    if (this.currentStepValue >= this.stepsValue.length - 1) {
      this.complete();
      return;
    }
    this.currentStepValue += 1;
    this._render();
    this._persistAdvance();
  }

  _teardown() {
    this._hidePopover();
    this._hideSpotlight();
    if (this.hasWelcomeModalTarget) {
      this.welcomeModalTarget.classList.remove("pt-modal--visible");
      this.welcomeModalTarget.hidden = true;
    }
    if (this.hasFinalModalTarget) {
      this.finalModalTarget.classList.remove("pt-modal--visible");
      this.finalModalTarget.hidden = true;
    }
    document.body.classList.remove("pt-no-scroll");
  }

  _handleResize() {
    const step = this.stepsValue[this.currentStepValue];
    if (!step || step.variant === "modal") return;
    const targetEl = step.selector ? document.querySelector(step.selector) : null;
    if (targetEl) {
      this._positionSpotlight(targetEl);
      this._popper?.update();
    }
  }

  _handleKey(event) {
    // Welcome modal : Esc skip, Enter start
    if (this.currentStepValue === 0 && this._welcomeVisible()) {
      if (event.key === "Escape") this.skip();
      if (event.key === "Enter")  this.start();
      return;
    }
    // Final modal : Enter / Esc complete
    if (this._finalVisible()) {
      if (event.key === "Enter" || event.key === "Escape") this.complete();
      return;
    }
    // Popover : Esc skip, flèches navigation
    if (event.key === "Escape")    this.skip();
    if (event.key === "ArrowRight") this.next();
    if (event.key === "ArrowLeft")  this.prev();
  }

  _welcomeVisible() {
    return this.hasWelcomeModalTarget && !this.welcomeModalTarget.hidden;
  }

  _finalVisible() {
    return this.hasFinalModalTarget && !this.finalModalTarget.hidden;
  }

  _persistAdvance() {
    if (!this.advanceUrlValue) return;
    fetch(this.advanceUrlValue, {
      method:  "POST",
      headers: this._headers(),
      body:    JSON.stringify({ step: this.currentStepValue }),
    }).catch(() => {});
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    return {
      "Content-Type": "application/json",
      "Accept":       "application/json",
      "X-CSRF-Token": csrf,
    };
  }
}
